/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { FunctionComponent, VNode } from 'preact';
import { HTMLAttributes } from 'preact/compat';
import { classes, VComponent } from '@adobe-commerce/elsie/lib';
import { Divider, Skeleton, SkeletonRow } from '@adobe-commerce/elsie/components';

import '@/cart/components/CartSummaryList/CartSummaryList.css';
export interface CartSummaryListProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'loading'> {
  heading?: VNode | null;
  footer?: VNode | null;
  emptyCart: VNode;
  products?: VNode | null;
  outOfStockMessage?: VNode | null;
  loading?: boolean;
  variant?: 'primary' | 'secondary';
}

export const CartSummaryList: FunctionComponent<CartSummaryListProps> = ({
  className,
  children,
  heading,
  footer,
  emptyCart,
  products,
  outOfStockMessage,
  variant = 'primary',
  loading = true,
  ...props
}) => {
  return (
    <div
      {...props}
      className={classes([
        'cart-cart-summary-list',
        className,
        `cart-cart-summary-list__background--${variant}`,
      ])}
    >
      {loading ? (
        <CartSummaryListSkeleton />
      ) : (
        <>
          {(heading || outOfStockMessage) && (
            <div
              data-testid="cart-summary-list-heading-wrapper"
              className={classes([
                'cart-cart-summary-list__heading',
                ['cart-cart-summary-list__heading--full-width', !products],
              ])}
            >
              {heading && (
                <>
                  <VComponent
                    node={heading}
                    className="cart-cart-summary-list__heading-text"
                  />
                  <Divider
                    variant="primary"
                    className={classes([
                      'cart-cart-summary-list__heading-divider',
                    ])}
                  />
                </>
              )}

              {outOfStockMessage && (
                <VComponent
                  node={outOfStockMessage}
                  className="cart-cart-summary-list__out-of-stock-message"
                />
              )}
            </div>
          )}
          <div
            className={classes([
              'cart-cart-summary-list__content',
              ['cart-cart-summary-list__content--empty', !products],
            ])}
          >
            {products || (
              <VComponent
                node={emptyCart}
                className="cart-cart-summary-list__empty-cart"
              />
            )}
          </div>

          {footer && (
            <>
              <Divider
                variant="primary"
                className={classes(['cart-cart-summary-list__footer-divider'])}
              />
              <VComponent
                node={footer}
                className="cart-cart-summary-list__footer-text"
              />
            </>
          )}
        </>
      )}
    </div>
  );
};

const CartSummaryListSkeleton: FunctionComponent = () => {
  return (
    <Skeleton
      data-testid="cart-summary-list-skeleton"
      className="cart-cart-summary-list__skeleton"
      rowGap="medium"
    >
      <SkeletonRow
        variant="row"
        size="xlarge"
        fullWidth={true}
        lines={3}
        multilineGap="small"
      />
    </Skeleton>
  );
};
